import { wsServerEvent } from '@planuze/admin-contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useChatStore } from '../stores/chat-store.js';

type ConnectionState = 'connecting' | 'open' | 'reconnecting' | 'closed';

/* Mesma origem do app (Vite proxy `/v1` com ws:true em dev; gateway em prod). O cookie
   HttpOnly de staff vai no handshake automaticamente (§4.9). */
function wsUrl(ticketId: string): string {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/v1/admin/chat/ws/${encodeURIComponent(ticketId)}`;
}

/* Conexão WebSocket ao SupportChatDO (ADR-0352) com reconnect exponencial (1s→30s,
   máx. 5 tentativas) e cleanup em unmount (§4.5). Eventos validados por `wsServerEvent`.
   Mensagem do staff é otimista; o echo do DO substitui a pendente (chat-store). */
export function useChatWebsocket(ticketId: string) {
  const queryClient = useQueryClient();
  const [connectionState, setConnectionState] = useState<ConnectionState>('connecting');
  const wsRef = useRef<WebSocket | null>(null);
  const attempts = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closedByUs = useRef(false);

  const connect = useCallback(() => {
    const ws = new WebSocket(wsUrl(ticketId));
    wsRef.current = ws;
    ws.onopen = () => {
      attempts.current = 0;
      setConnectionState('open');
    };
    ws.onmessage = (event: MessageEvent) => {
      let raw: unknown;
      try {
        raw = JSON.parse(typeof event.data === 'string' ? event.data : '');
      } catch {
        return;
      }
      const parsed = wsServerEvent.safeParse(raw);
      if (!parsed.success) return;
      const store = useChatStore.getState();
      const ev = parsed.data;
      switch (ev.type) {
        case 'history':
          store.setHistory(ev.messages);
          break;
        case 'message':
          /* `clientMessageId` (ADR-0434) só vem no eco da própria mensagem → casa a bolha
             otimista exata (id estável), sem heurística por conteúdo. */
          store.addMessage(ev.message, ev.clientMessageId);
          break;
        case 'typing':
          store.setTyping(ev.senderId, ev.isTyping);
          break;
        case 'presence':
          store.setPresence(ev.userOnline);
          break;
        case 'status':
          /* atender/fechar/auto-atender (ADR-0433): atualiza detail (badge/cabeçalho) +
             lista sem reload. O chat-store (mensagens via WS) não é tocado. */
          queryClient.invalidateQueries({ queryKey: ['tickets'] });
          break;
        case 'error':
          break;
      }
    };
    ws.onclose = () => {
      if (closedByUs.current) return;
      if (attempts.current >= 5) {
        setConnectionState('closed');
        return;
      }
      setConnectionState('reconnecting');
      const delay = Math.min(1000 * 2 ** attempts.current, 30_000);
      attempts.current += 1;
      timerRef.current = setTimeout(() => {
        connect();
      }, delay);
    };
  }, [ticketId, queryClient]);

  useEffect(() => {
    closedByUs.current = false;
    attempts.current = 0;
    useChatStore.getState().reset();
    connect();
    return () => {
      closedByUs.current = true;
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      wsRef.current?.close(1000, 'unmount');
      wsRef.current = null;
    };
  }, [connect]);

  const sendMessage = useCallback(
    (content: string, attachments: readonly string[] = []): void => {
      const ws = wsRef.current;
      if (ws === null || ws.readyState !== WebSocket.OPEN) return;
      /* Id de correlação otimista (ADR-0434): mesmo id na bolha e no payload → o DO ecoa
         de volta e o chat-store casa exatamente, sem heurística por conteúdo. */
      const clientMessageId = `pending-${crypto.randomUUID()}`;
      ws.send(JSON.stringify({ type: 'message', content, attachments, clientMessageId }));
      useChatStore.getState().addOptimistic({
        id: clientMessageId,
        ticketId,
        senderType: 'staff',
        senderId: 'self',
        content,
        attachments: [...attachments],
        createdAt: new Date().toISOString(),
        pending: true,
      });
    },
    [ticketId],
  );

  const sendTyping = useCallback((isTyping: boolean): void => {
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'typing', isTyping }));
    }
  }, []);

  const markRead = useCallback((messageId: string): void => {
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'mark_read', messageId }));
    }
  }, []);

  const messages = useChatStore((s) => s.messages);
  const typingSenders = useChatStore((s) => s.typingSenders);
  return { messages, typingSenders, connectionState, sendMessage, sendTyping, markRead };
}
